import React, { useState, useRef, useEffect } from 'react';
import {
  StickyNote,
  Pencil,
  Minus,
  ArrowRight,
  Circle,
  Link2,
  Sticker,
  ImagePlus,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Plus,
  Table,
  CheckSquare,
  Trash2,
  Expand,
  Copy,
  Palette,
  FolderOpen,
  FolderPlus,
  Check,
  X,
  Image as ImageIcon,
  Globe,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { toast } from 'sonner';
import { uploadImageToServer, fetchImageFromUrl, resolveImageUrl, getImageNaturalSize, computeDropSize } from '../lib/imageUtils';

const STICKER_TYPES = [
  { type: 'arrow-right', icon: '→', label: 'Arrow' },
  { type: 'arrow-down', icon: '↓', label: 'Down' },
  { type: 'circle', icon: '○', label: 'Circle' },
  { type: 'square', icon: '□', label: 'Square' },
  { type: 'star', icon: '☆', label: 'Star' },
  { type: 'heart', icon: '♡', label: 'Heart' },
];

const NOTE_COLORS = [
  { name: 'default', bg: 'bg-card', border: 'border-border' },
  { name: 'pink', bg: 'bg-secondary/30', border: 'border-secondary' },
  { name: 'lavender', bg: 'bg-accent/30', border: 'border-accent' },
  { name: 'mint', bg: 'bg-primary/30', border: 'border-primary' },
  { name: 'peach', bg: 'bg-muted/50', border: 'border-muted-foreground' },
];

const COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#eab308', '#8b5cf6', '#f97316',
  '#ec4899', '#14b8a6', '#000000', '#6b7280'
];

const DRAWING_TOOLS = [
  { tool: 'freehand', Icon: Pencil, label: 'Freehand' },
  { tool: 'line', Icon: Minus, label: 'Line' },
  { tool: 'arrow', Icon: ArrowRight, label: 'Arrow' },
  { tool: 'ellipse', Icon: Circle, label: 'Ellipse' },
];

const STROKE_WIDTHS = [
  { width: 2, label: 'Thin' },
  { width: 4, label: 'Medium' },
  { width: 8, label: 'Thick' },
];

export const ToolStrip = ({
  onAddNote,
  drawingTool,
  setDrawingTool,
  addSticker,
  onAddSource,
  addNoteSticker,
  folders,
  notes,
  onAddImage,
  onAddTable,
  onAddTodo,
  activeNote,
  onClearActiveNote,
  updateNote,
  deleteNote,
  onOpenReaderMode,
  noteToolsKey = 0,
  // Folder view props
  showFolderView = false,
  onAddFolder,
  activeFolder,
  updateFolder,
  deleteFolder,
  onClearActiveFolder,
  folderToolsKey = 0,
}) => {
  const [expandedTool, setExpandedTool] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [stickerColor, setStickerColor] = useState('#3b82f6');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [drawingColor, setDrawingColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [folderNameDialog, setFolderNameDialog] = useState(null); // { mode: 'create' | 'rename', value: string }
  const [folderDeleteConfirmOpen, setFolderDeleteConfirmOpen] = useState(false);
  const noteImageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderImageInputRef = useRef(null);
  const dockRef = useRef(null);
  const rightBarRef = useRef(null);

  // Open note tools sub-view when triggered from a note card's three-dots button
  useEffect(() => {
    if (noteToolsKey > 0) setExpandedTool('notetools');
  }, [noteToolsKey]);

  // Open folder tools sub-view when triggered from a folder item's ... button
  useEffect(() => {
    if (folderToolsKey > 0) setExpandedTool('foldertools');
  }, [folderToolsKey]);

  // Close sub-dock when clicking outside the dock (e.g. on the canvas)
  useEffect(() => {
    const handleClickOutside = (e) => {
      const outsideDock = dockRef.current && !dockRef.current.contains(e.target);
      const outsideRightBar = !rightBarRef.current || !rightBarRef.current.contains(e.target);
      if (outsideDock && outsideRightBar) {
        setExpandedTool(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddSticker = (type) => {
    addSticker({
      type,
      position: { x: 200 + Math.random() * 300, y: 150 + Math.random() * 200 },
      size: { width: 60, height: 60 },
      rotation: 0,
      color: stickerColor,
    });
    toast.success('Sticker added!');
  };

  const handleAddSourceClick = () => {
    if (!onAddSource) return;
    let url = newLinkUrl.trim();
    const title = newLinkTitle.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    onAddSource({ url, title });
    setNewLinkUrl('');
    setNewLinkTitle('');
    setExpandedTool(null);
    toast.success('Source added to canvas!');
  };

  const handleImageClick = () => fileInputRef.current?.click();

  // Same aspect ratio as the source, clamped to a consistent canvas footprint.
  const resolveDropSize = async (imageData) => {
    const resolvedUrl = await resolveImageUrl(imageData);
    const naturalSize = await getImageNaturalSize(resolvedUrl);
    return computeDropSize(naturalSize?.width, naturalSize?.height);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      toast.loading('Processing image...');
      const imageData = await uploadImageToServer(file);
      if (onAddImage) {
        onAddImage({
          type: 'image', data: imageData,
          position: { x: 150 + Math.random() * 200, y: 100 + Math.random() * 150 },
          size: await resolveDropSize(imageData),
        });
      }
      toast.dismiss();
      toast.success('Image added to canvas!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to process image');
    }
    e.target.value = '';
  };

  const handleAddTable = () => {
    if (onAddTable) {
      onAddTable({
        type: 'table',
        position: { x: 150 + Math.random() * 200, y: 100 + Math.random() * 150 },
        size: { width: 400, height: 200 },
        rows: 3, cols: 3,
        data: [['','',''],['','',''],['','','']],
      });
      toast.success('Table added!');
    }
  };

  const handleAddTodo = () => {
    if (onAddTodo) {
      onAddTodo({
        type: 'todo',
        position: { x: 150 + Math.random() * 200, y: 100 + Math.random() * 150 },
        size: { width: 280, height: 200 },
        title: 'Todo List',
        items: [{ id: Date.now(), text: '', completed: false }],
      });
      toast.success('Todo list added!');
    }
  };

  if (isCollapsed) {
    return (
      <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40">
        <Button
          variant="secondary"
          size="icon"
          className="rounded-l-none rounded-r-md h-12 w-6 shadow-lg"
          onClick={() => setIsCollapsed(false)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Dock width expands only for the links form
  const dockWidth = (expandedTool === 'links' || expandedTool === 'imageurl') ? 'w-44' : 'w-14';

  return (
    <TooltipProvider delayDuration={200}>
      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40 flex gap-2">

        {/* ── Main Dock ── */}
        <div
          ref={dockRef}
          className={`bg-card/90 backdrop-blur-md rounded-xl shadow-xl border border-border/50 p-2 flex flex-col gap-1 ${dockWidth} transition-all duration-150`}
        >

          {/* ── FOLDER CANVAS MAIN TOOLS ── */}
          {showFolderView && !expandedTool && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 mb-1" onClick={() => setIsCollapsed(true)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Collapse toolbar</p></TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => setFolderNameDialog({ mode: 'create', value: '' })}
                  >
                    <FolderPlus className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>New folder</p></TooltipContent>
              </Tooltip>
            </>
          )}

          {/* ── FOLDER TOOLS SUB-VIEW ── */}
          {expandedTool === 'foldertools' && activeFolder && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 mb-1" onClick={() => { setExpandedTool(null); onClearActiveFolder?.(); }}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Back</p></TooltipContent>
              </Tooltip>

              <p className="text-[9px] text-muted-foreground truncate text-center px-1 -mt-0.5 mb-0.5">{activeFolder.name}</p>

              {/* Rename */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => setFolderNameDialog({ mode: 'rename', value: activeFolder.name })}>
                    <Pencil className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Rename folder</p></TooltipContent>
              </Tooltip>

              {/* Cover image */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => folderImageInputRef.current?.click()}>
                    <ImageIcon className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Set cover image</p></TooltipContent>
              </Tooltip>
              <input
                ref={folderImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !activeFolder) return;
                  try {
                    const { uploadImageToServer } = await import('../lib/imageUtils');
                    const data = await uploadImageToServer(file);
                    updateFolder(activeFolder.id, { image: data });
                    toast.success('Cover image updated!');
                  } catch { toast.error('Failed to process image'); }
                  e.target.value = '';
                }}
              />

              {/* Delete */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setFolderDeleteConfirmOpen(true)}>
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Delete folder</p></TooltipContent>
              </Tooltip>
            </>
          )}

          {/* ── MAIN TOOLS VIEW ── */}
          {!showFolderView && !expandedTool && (
            <>
              {/* Collapse */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 mb-1" onClick={() => setIsCollapsed(true)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Collapse toolbar</p></TooltipContent>
              </Tooltip>

              {/* Add Note */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => { onAddNote(); toast.success('Note created!'); }}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('application/anotequest-item', JSON.stringify({ kind: 'note' })); e.dataTransfer.effectAllowed = 'copy'; }}
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Add a note</p></TooltipContent>
              </Tooltip>

              {/* Draw */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={drawingTool ? 'default' : 'ghost'} size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => {
                      setExpandedTool('draw');
                      if (!drawingTool) setDrawingTool('freehand');
                    }}
                  >
                    <Pencil className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Draw</p></TooltipContent>
              </Tooltip>

              {/* Stickers */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => setExpandedTool('stickers')}
                  >
                    <Sticker className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Stickers</p></TooltipContent>
              </Tooltip>

              {/* Note Sticker */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => { addNoteSticker(); toast.success('Note sticker added!'); }}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('application/anotequest-item', JSON.stringify({ kind: 'noteSticker' })); e.dataTransfer.effectAllowed = 'copy'; }}
                  >
                    <StickyNote className="h-5 w-5 text-amber-400" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Add sticky note</p></TooltipContent>
              </Tooltip>

              {/* Upload Image */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg" onClick={handleImageClick}>
                    <ImagePlus className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Upload image</p></TooltipContent>
              </Tooltip>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

              {/* Image from URL */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg" onClick={() => setExpandedTool('imageurl')}>
                    <Globe className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Add image from URL</p></TooltipContent>
              </Tooltip>

              {/* Add Table */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={handleAddTable}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('application/anotequest-item', JSON.stringify({ kind: 'table' })); e.dataTransfer.effectAllowed = 'copy'; }}
                  >
                    <Table className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Add a table</p></TooltipContent>
              </Tooltip>

              {/* Add Todo */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={handleAddTodo}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('application/anotequest-item', JSON.stringify({ kind: 'todo' })); e.dataTransfer.effectAllowed = 'copy'; }}
                  >
                    <CheckSquare className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Add todo list</p></TooltipContent>
              </Tooltip>

              {/* Links */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => setExpandedTool('links')}
                  >
                    <Link2 className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Add links / sources</p></TooltipContent>
              </Tooltip>

            </>
          )}

          {/* ── DRAW SUB-TOOLS VIEW ── */}
          {expandedTool === 'draw' && (
            <>
              {/* Back */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 mb-1" onClick={() => setExpandedTool(null)}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Back</p></TooltipContent>
              </Tooltip>

              {DRAWING_TOOLS.map(({ tool, Icon, label }) => (
                <Tooltip key={tool}>
                  <TooltipTrigger asChild>
                    <Button
                      variant={drawingTool === tool ? 'default' : 'ghost'}
                      size="icon" className="h-10 w-10 rounded-lg"
                      onClick={() => setDrawingTool(tool)}
                    >
                      <Icon className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right"><p>{label}</p></TooltipContent>
                </Tooltip>
              ))}
            </>
          )}

          {/* ── STICKERS SUB-TOOLS VIEW ── */}
          {expandedTool === 'stickers' && (
            <>
              {/* Back */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 mb-1" onClick={() => setExpandedTool(null)}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Back</p></TooltipContent>
              </Tooltip>

              {STICKER_TYPES.map(({ type, icon, label }) => (
                <Tooltip key={type}>
                  <TooltipTrigger asChild>
                    <button
                      className="h-10 w-10 rounded-lg border border-border hover:bg-accent hover:border-primary transition-all flex items-center justify-center text-xl active:scale-95"
                      onClick={() => handleAddSticker(type)}
                      style={{ color: stickerColor }}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('application/anotequest-item', JSON.stringify({ kind: 'sticker', type })); e.dataTransfer.effectAllowed = 'copy'; }}
                    >
                      {icon}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right"><p>{label}</p></TooltipContent>
                </Tooltip>
              ))}

              {/* Color swatches */}
              <div className="grid grid-cols-2 gap-1 mt-1 px-0.5">
                {COLORS.map(color => (
                  <button
                    key={color}
                    className={`h-4 w-4 rounded border-2 transition-all ${stickerColor === color ? 'border-primary ring-1 ring-primary/50' : 'border-transparent'}`}
                    style={{ background: color }}
                    onClick={() => setStickerColor(color)}
                  />
                ))}
              </div>
            </>
          )}

          {/* ── NOTE TOOLS SUB-VIEW ── */}
          {expandedTool === 'notetools' && activeNote && (
            <>
              {/* Back */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 mb-1" onClick={() => setExpandedTool(null)}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Back</p></TooltipContent>
              </Tooltip>

              <p className="text-[9px] text-muted-foreground truncate text-center px-1 -mt-0.5 mb-0.5">{activeNote.title || 'Untitled'}</p>

              {/* Reader */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => { onOpenReaderMode(activeNote.id); setExpandedTool(null); }}>
                    <Expand className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Reader mode</p></TooltipContent>
              </Tooltip>

              {/* Add Image */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => noteImageInputRef.current?.click()}>
                    <ImageIcon className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Add image</p></TooltipContent>
              </Tooltip>
              <input
                ref={noteImageInputRef} type="file" accept="image/*" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !activeNote) return;
                  try {
                    const data = await uploadImageToServer(file);
                    updateNote(activeNote.id, { images: [...(activeNote.images || []), { id: Date.now(), data }] });
                    toast.success('Image added!');
                  } catch { toast.error('Failed to add image'); }
                  e.target.value = '';
                }}
              />

              {/* Duplicate */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg"
                    onClick={() => {
                      const base = activeNote.position || { x: 200, y: 160 };
                      onAddNote({ title: activeNote.title || 'New Note', content: activeNote.content, size: activeNote.size, color: activeNote.color, images: [...(activeNote.images || [])], position: { x: base.x + 40, y: base.y + 40 }, folderId: activeNote.folderId });
                      toast.success('Note duplicated');
                    }}>
                    <Copy className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Duplicate</p></TooltipContent>
              </Tooltip>

              {/* Color */}
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg">
                        <Palette className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right"><p>Color</p></TooltipContent>
                </Tooltip>
                <DropdownMenuContent side="right">
                  {NOTE_COLORS.map(color => (
                    <DropdownMenuItem key={color.name}
                      onClick={() => { updateNote(activeNote.id, { color: color.name }); toast.success('Color updated!'); }}
                      className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-sm ${color.bg} ${color.border} border-2`} />
                      <span className="capitalize">{color.name}</span>
                      {activeNote.color === color.name && <Check className="h-4 w-4 ml-auto" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Folder */}
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg">
                        <FolderOpen className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right"><p>Folder</p></TooltipContent>
                </Tooltip>
                <DropdownMenuContent side="right">
                  <DropdownMenuItem onClick={() => { updateNote(activeNote.id, { folderId: null }); toast.success('Moved to All Notes'); }}>
                    <span>No Folder</span>
                    {!activeNote.folderId && <Check className="h-4 w-4 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {folders.map(folder => (
                    <DropdownMenuItem key={folder.id}
                      onClick={() => { updateNote(activeNote.id, { folderId: folder.id }); toast.success('Moved to folder!'); }}>
                      <span>{folder.name}</span>
                      {activeNote.folderId === folder.id && <Check className="h-4 w-4 ml-auto" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Delete */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => { deleteNote(activeNote.id); onClearActiveNote(); setExpandedTool(null); toast.success('Note deleted'); }}>
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right"><p>Delete note</p></TooltipContent>
              </Tooltip>
            </>
          )}

          {/* ── IMAGE FROM URL SUB-VIEW ── */}
          {expandedTool === 'imageurl' && (
            <>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-muted-foreground">Image URL</h3>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setExpandedTool(null)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://i.pinimg.com/..."
                className="h-7 text-xs"
                onKeyDown={async (e) => {
                  if (e.key !== 'Enter') return;
                  const url = imageUrl.trim();
                  if (!url) return;
                  try {
                    toast.loading('Fetching image...');
                    const imageData = await fetchImageFromUrl(url);
                    if (onAddImage) {
                      onAddImage({
                        type: 'image', data: imageData,
                        position: { x: 150 + Math.random() * 200, y: 100 + Math.random() * 150 },
                        size: await resolveDropSize(imageData),
                      });
                    }
                    toast.dismiss();
                    toast.success('Image added!');
                    setImageUrl('');
                    setExpandedTool(null);
                  } catch (err) {
                    toast.dismiss();
                    toast.error(err.message || 'Failed to fetch image');
                  }
                }}
              />
              <Button
                size="sm" className="w-full h-7 text-xs mt-0.5"
                disabled={!imageUrl.trim()}
                onClick={async () => {
                  const url = imageUrl.trim();
                  if (!url) return;
                  try {
                    toast.loading('Fetching image...');
                    const imageData = await fetchImageFromUrl(url);
                    if (onAddImage) {
                      onAddImage({
                        type: 'image', data: imageData,
                        position: { x: 150 + Math.random() * 200, y: 100 + Math.random() * 150 },
                        size: await resolveDropSize(imageData),
                      });
                    }
                    toast.dismiss();
                    toast.success('Image added!');
                    setImageUrl('');
                    setExpandedTool(null);
                  } catch (err) {
                    toast.dismiss();
                    toast.error(err.message || 'Failed to fetch image');
                  }
                }}
              >
                Add to canvas
              </Button>
            </>
          )}

          {/* ── LINKS SUB-TOOLS VIEW ── */}
          {expandedTool === 'links' && (
            <>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-muted-foreground">Add Link</h3>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setExpandedTool(null)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <Input
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="h-7 text-xs"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddSourceClick(); }}
              />
              <Input
                value={newLinkTitle}
                onChange={(e) => setNewLinkTitle(e.target.value)}
                placeholder="Optional title"
                className="h-7 text-xs"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddSourceClick(); }}
              />
              <Button
                size="sm" className="w-full h-7 text-xs mt-0.5"
                onClick={handleAddSourceClick}
                disabled={!newLinkUrl.trim()}
              >
                Add to canvas
              </Button>
            </>
          )}
        </div>

      </div>

      {/* Right drawing bar — appears when a drawing tool is active */}
      {drawingTool && (
        <div
          ref={rightBarRef}
          className="fixed right-4 top-1/2 -translate-y-1/2 z-40 bg-card/90 backdrop-blur-md rounded-xl shadow-xl border border-border/50 p-2 flex flex-col gap-1 w-14 animate-in slide-in-from-right-2"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost" size="icon" className="h-8 w-8 mb-1"
                onClick={() => { setDrawingTool(null); setExpandedTool(null); }}
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left"><p>Stop drawing</p></TooltipContent>
          </Tooltip>

          {STROKE_WIDTHS.map(({ width, label }) => (
            <Tooltip key={width}>
              <TooltipTrigger asChild>
                <button
                  className={`h-10 w-10 rounded-lg flex items-center justify-center transition-all border ${strokeWidth === width ? 'bg-primary/20 border-primary' : 'border-border hover:bg-accent'}`}
                  onClick={() => setStrokeWidth(width)}
                >
                  <div className="rounded-full bg-foreground" style={{ width: Math.min(width * 2.5, 20), height: Math.min(width * 2.5, 20) }} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left"><p>{label}</p></TooltipContent>
            </Tooltip>
          ))}

          <div className="border-t border-border/50 my-1" />

          <div className="grid grid-cols-2 gap-1 px-0.5">
            {COLORS.map(color => (
              <Tooltip key={color}>
                <TooltipTrigger asChild>
                  <button
                    className={`h-5 w-5 rounded border-2 transition-all ${drawingColor === color ? 'border-primary ring-1 ring-primary/50 scale-110' : 'border-transparent hover:scale-110'}`}
                    style={{ background: color }}
                    onClick={() => setDrawingColor(color)}
                  />
                </TooltipTrigger>
                <TooltipContent side="left"><p style={{ color }}>{color}</p></TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      {/* New / rename folder dialog */}
      <Dialog open={!!folderNameDialog} onOpenChange={(open) => !open && setFolderNameDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{folderNameDialog?.mode === 'rename' ? 'Rename folder' : 'New folder'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              value={folderNameDialog?.value || ''}
              onChange={(e) => setFolderNameDialog((d) => ({ ...d, value: e.target.value }))}
              placeholder="Folder name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                const name = folderNameDialog?.value.trim();
                if (!name) return;
                if (folderNameDialog.mode === 'rename') updateFolder(activeFolder.id, { name });
                else onAddFolder(name);
                setFolderNameDialog(null);
              }}
            />
            <Button
              className="w-full"
              disabled={!folderNameDialog?.value?.trim()}
              onClick={() => {
                const name = folderNameDialog.value.trim();
                if (folderNameDialog.mode === 'rename') updateFolder(activeFolder.id, { name });
                else onAddFolder(name);
                setFolderNameDialog(null);
              }}
            >
              {folderNameDialog?.mode === 'rename' ? 'Save' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete folder confirmation */}
      <AlertDialog open={folderDeleteConfirmOpen} onOpenChange={setFolderDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete folder "{activeFolder?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Items inside will be moved to root. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteFolder(activeFolder.id);
                setFolderDeleteConfirmOpen(false);
                setExpandedTool(null);
                onClearActiveFolder?.();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};

export default ToolStrip;
